import React from 'react';
import debounce from 'lodash.debounce';
import { RouteComponentProps } from 'react-router-dom';
import NotFound from '../app/pages/errors/NotFound/NotFound';
import Loader from '../app/shared/components/Loader';
import trace from '../utilities/trace';

const setFirebirdWidth = debounce(() => {
    const navigation: any = window.top.document.querySelector('#layout .gd-navigation');
    const width = navigation ? `calc(100% - ${navigation.offsetWidth}px)` : '100%';
    const frame = window.firebird.document.querySelector('#midnav-frame');
    if (frame) frame.style.width = width;
}, 100);

const replaceParameters = (url, params) => Object.keys(params).reduce((u, k) => u.replace(':' + k, params[k]), url);

const midnavWhitelist = ['/home', '/inbox', '/market', '/journal-pro', '/webshop', '/webshop-plus', '/website'];

export const refreshCurrentLegacyFrame = (frame = 'midnav') => {
    window.top.firebird.Econ.frames[frame].location.reload();
};

interface Props extends RouteComponentProps {
    legacyUrl: string;
    onNavigated?: (frame: string) => void;
}

interface State {
    hasNavigated: boolean;
    isLoading: boolean;
    notFound: boolean;
}

export const refreshCurrentLegacyPage = (frame = 'midnav') => {
    window.top.firebird.Econ.frames[frame].location.reload();
};

export const redirectToLegacyPage = (legacyUrl, frame = 'content') => {
    window.top.firebird.Econ.frames[frame].location.href = `${process.env.REACT_APP_ECONOMIC_PATH}/${legacyUrl}`;
};

export default class LegacyFrameNavigator extends React.Component<Props, State> {
    state = { hasNavigated: false, isLoading: true, notFound: false };

    componentDidMount() {
        if (!window.top.Econ) {
            window.top.addEventListener('firebird:ready', this.handleFirebirdReady);
        } else {
            this.bindFrameListeners('on');
            this.navigate();
        }

        window.top.addEventListener('resize', setFirebirdWidth);
    }

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        const changedUrl = this.props.location.pathname !== nextProps.location.pathname;
        const reloadedCurrentUrl = !changedUrl && this.props.location.key !== nextProps.location.key;
        const changedHash = !!this.props.location.hash && this.props.location.hash !== nextProps.location.hash;

        if (
            changedUrl ||
            (reloadedCurrentUrl && !changedHash) ||
            this.state.isLoading !== nextState.isLoading ||
            this.state.notFound !== nextState.notFound
        ) {
            return true;
        }

        return false;
    }

    componentDidUpdate(prevProps: Props) {
        if (window.top.Econ) {
            if (
                this.props.location.key !== prevProps.location.key &&
                (this.props.legacyUrl !== prevProps.legacyUrl || this.props.location.hash === '')
            ) {
                this.setState({ hasNavigated: false, isLoading: true }, this.navigate);
            }
        }
    }

    componentWillUnmount() {
        (document.querySelector('iframe[name=firebird]') as any).style.display = 'none';
        window.top.removeEventListener('resize', setFirebirdWidth);
        window.top.removeEventListener('firebird:ready', this.handleFirebirdReady);

        if (window.top.Econ) {
            this.bindFrameListeners('off');
        }
    }

    render() {
        if (this.state.isLoading) {
            return <Loader />;
        } else if (this.state.notFound) {
            return <NotFound />;
        }

        return null;
    }

    handleFirebirdReady = (event: any) => {
        if (event.detail.isGettingStartedSplashScreenAvailable) {
            this.displayFrameForGettingStartedGuide();
        } else {
            this.bindFrameListeners('on');
            this.navigate();
        }
    };

    displayFrameForGettingStartedGuide = () => {
        (document.querySelector('iframe[name=firebird]') as any).style.display = 'block';
        this.setState({ isLoading: false });
    };

    notFound = () => {
        (document.querySelector('iframe[name=firebird]') as any).style.display = 'none';
        this.setState({ isLoading: false, notFound: true });
    };

    navigate = () => {
        const { match, legacyUrl, location } = this.props;
        const frame = midnavWhitelist.filter(x => match.path.startsWith(x)).length > 0 ? 'midnav' : 'content';

        const frames = window.firebird.Econ.require('frames');

        frames.navigate('midnav', 'midnav.aspx').then(() => {
            if (frames.inMidnav('content')) {
                frames.setRightFrames(true);
            }

            const url = replaceParameters(legacyUrl, match.params);
            const queryString = location.search
                ? url.includes('?')
                    ? location.search.replace('?', '&')
                    : location.search
                : undefined;
            window.top.firebird.Econ.frames[frame].location = url + (queryString || '');

            trace.status(`Navigated to \`${match.url}\` legacy route...`);
            (document.querySelector('iframe[name=firebird]') as any).style.display = 'block';
            this.setState({ hasNavigated: true });
        });
    };

    bindFrameListeners = (action: string) => {
        window.top.Econ[action]('frame.load.midnav', this.handleMidnavReady);
        window.top.Econ[action]('frame.load.content', this.handleContentReady);
        window.top.Econ[action]('frame.load.applet', this.handleAppletReady);
    };

    handleMidnavReady = () => {
        const applet = window.firebird.midnav.document.querySelector('iframe[name=applet]');
        const content = window.firebird.midnav.document.querySelector('iframe[name=content]');

        if (!applet && !content) {
            // trace.info('midnav frame ready');
            this.setRouteAsReady();

            if (this.props.onNavigated) {
                this.props.onNavigated('midnav');
            }
        }
    };

    handleContentReady = () => {
        if (this.state.hasNavigated) {
            const applet = window.firebird.midnav.document.querySelector('iframe[name=applet]');

            // some pages load both content and applet, this is the only reliable way to decide which to wait for
            if (applet.style.height === '0px') {
                //  trace.info('content frame ready');
                this.setRouteAsReady();

                if (this.props.onNavigated) {
                    this.props.onNavigated('content');
                }
            }
        }
    };

    handleAppletReady = () => {
        if (this.state.hasNavigated) {
            // trace.info('applet frame ready');
            this.setRouteAsReady();
        }
    };

    // this occurs when midnav and/or applet frames are 'ready'
    setRouteAsReady = () => {
        setFirebirdWidth();
        this.setState({ isLoading: false });
    };
}
